// The account contract: what the browser may ask about an account's
// membership and the shapes of the answers, written once with Effect's typed
// HTTP API. The server builds its handlers from it (server/account/service.ts)
// and the client its calls (./client.ts), so the two cannot drift; the tests
// run both in one process with no socket between them.

import { Schema } from "effect";
import { HttpApi, HttpApiEndpoint, HttpApiGroup, HttpApiSchema } from "effect/http-api";

/**
 * The two ways to pay for the membership: US cents per billing, the yen a
 * buyer in Japan pays instead (the price's JPY currency option), and the
 * months it buys.
 */
export const PLANS = {
  month: { amount: 799, yen: 1280, months: 1 },
  year: { amount: 5988, yen: 9800, months: 12 },
} as const;
export type Plan = keyof typeof PLANS;

/**
 * Where the payment provider sends a visitor back to, on this origin, after
 * checkout or the billing portal. Arriving here activates nothing: the
 * provider's webhook does.
 */
export const BILLING_RETURN_PATH = "/studio/";

/**
 * A page a checkout may return its buyer to in place of the room: a game's
 * page (`/games/<label>`, or `/<lang>/games/<label>`), where a visitor who
 * asked for a past version met the paywall. Anything else returns to the room.
 */
export const BILLING_BACK = /^\/(?:[a-z]{2}\/)?games\/[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/;

/** Where a checkout sends its buyer afterwards: `back` when it is a page of BILLING_BACK, the room otherwise. */
export function billingReturnPath(back?: string | null): string {
  return typeof back === "string" && BILLING_BACK.test(back) ? back : BILLING_RETURN_PATH;
}

const PlanName = Schema.Literals(["month", "year"]);

/** An account's sign-in and membership as its own browser may see them. */
export const AccountState = Schema.Struct({
  /** Null when nobody is signed in with an account. */
  user: Schema.NullOr(Schema.Struct({ id: Schema.String, name: Schema.String, email: Schema.String })),
  /** Whether time paid for is still running. */
  active: Schema.Boolean,
  /** The end of the paid time, in epoch milliseconds; 0 when there never was any. */
  paidUntil: Schema.Number,
  cancelAtPeriodEnd: Schema.Boolean,
  plan: Schema.NullOr(PlanName),
  /** The provider's word for the subscription, or "free". */
  status: Schema.String,
  /** Whether this server is accepting new membership payments. Existing subscriptions can still be managed. */
  billingReady: Schema.Boolean,
  /** Whether the account is already known to the payment provider. */
  hasCustomer: Schema.Boolean,
});
export type AccountState = typeof AccountState.Type;

/** A refusal with a sentence fit to show. */
export class AccountError extends Schema.TaggedError<AccountError>()("AccountError", { message: Schema.String }) {}

// The session cookie reaches a handler as a header. A browser sends it on
// its own; the in-process client passes it explicitly.
const headers = { cookie: Schema.optional(Schema.String) };
const refusal = AccountError.pipe(HttpApiSchema.status(400));
const Redirect = Schema.Struct({ url: Schema.String });

export const AccountApi = HttpApi.make("NexusAccount").add(
  HttpApiGroup.make("account")
    .add(HttpApiEndpoint.get("state", "/api/account", { headers, success: AccountState, error: refusal }))
    // Where to send the visitor to pay for a plan.
    // `timeZone` and `mainland` say where the buyer is (BuyerPlace in src/core/seller.ts), with the country Cloudflare adds as a header.
    .add(HttpApiEndpoint.post("checkout", "/api/account/checkout", {
      headers: { ...headers, "cf-ipcountry": Schema.optional(Schema.String) },
      // `back` is the page the buyer returns to (billingReturnPath); absent, the room.
      payload: Schema.Struct({ plan: PlanName, timeZone: Schema.optional(Schema.String), mainland: Schema.optional(Schema.Boolean), back: Schema.optional(Schema.String) }),
      success: Redirect,
      error: refusal,
    }))
    // Where to send a customer to manage or cancel the subscription.
    .add(HttpApiEndpoint.post("portal", "/api/account/portal", { headers, success: Redirect, error: refusal })),
);
